import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckIcon from '@mui/icons-material/Check';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import React, { useState } from 'react';

import {
  buildWitnessExtension,
  WITNESS_CONNECTOR_PATH,
  witnessExtensionFileName,
} from './witness-extension';

import Button from '~/client/components/base/button';
import { Drawer } from '~/client/components/base/drawer';
import Text from '~/client/components/base/text';
import {
  guardianMcpEndpoint,
  mcpClientConfig,
} from '~/client/components/dashboard/guardian/agent-enrollments/connect-config';
import * as GQL from '~/graphql/generated/client';
import { useSnackbarContext } from '~c/components/base/snackbar';

type AssistantKey = 'claude' | 'claude-desktop' | 'claude-code' | 'mcp-client';

interface Assistant {
  key: AssistantKey;
  name: string;
  type: string;
  badge?: string;
  icon: React.ElementType;
  // Assistants that sign in with Fairly need the project's public address; the others use a connection key.
  signsIn: boolean;
  keyClient?: GQL.GuardianAssistantClient;
}

const ASSISTANTS: Assistant[] = [
  {
    key: 'claude',
    name: 'Claude',
    type: 'Web, desktop & mobile',
    badge: 'Recommended',
    icon: ChatBubbleOutlineIcon,
    signsIn: true,
  },
  {
    key: 'claude-desktop',
    name: 'Claude Desktop extension',
    type: 'Desktop',
    icon: DesktopWindowsOutlinedIcon,
    signsIn: false,
    keyClient: GQL.GuardianAssistantClient.ClaudeDesktop,
  },
  {
    key: 'claude-code',
    name: 'Claude Code',
    type: 'Terminal',
    icon: TerminalOutlinedIcon,
    signsIn: true,
  },
  {
    key: 'mcp-client',
    name: 'Other MCP client',
    type: 'Any',
    icon: HubOutlinedIcon,
    signsIn: false,
    keyClient: GQL.GuardianAssistantClient.McpClient,
  },
];

const copy = (text: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => {
      /* the value stays visible for a manual copy */
    });
  }
};

const Snippet: React.FC<{ text: string; label: string }> = ({
  text,
  label,
}) => (
  <Box sx={{ mt: 1, mb: 2 }}>
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        fontSize: 12,
      }}
    >
      {text}
    </Box>
    <Button outlined size="small" sx={{ mt: 1 }} onClick={() => copy(text)}>
      {label}
    </Button>
  </Box>
);

const IconTile: React.FC<{ icon: React.ElementType }> = ({ icon: Icon }) => (
  <Box
    sx={{
      width: 32,
      height: 32,
      flexShrink: 0,
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'background.paper',
    }}
  >
    <Icon sx={{ fontSize: 18 }} />
  </Box>
);

const Steps: React.FC<{ steps: string[] }> = ({ steps }) => (
  <Box component="ol" sx={{ pl: 2.5, my: 1 }}>
    {steps.map((step) => (
      <Box component="li" key={step} sx={{ mb: 0.75 }}>
        <Text sz="body14">{step}</Text>
      </Box>
    ))}
  </Box>
);

const isLocalAddress = (url: string) => {
  try {
    const { hostname, protocol } = new URL(url);
    return (
      protocol === 'http:' ||
      ['localhost', '127.0.0.1', '[::1]'].includes(hostname)
    );
  } catch {
    return false;
  }
};

interface ConnectDrawerProps {
  projectId: string;
  projectName: string;
  connectorUrl: string | null;
  orgConnectorUrl: string | null;
  onClose: () => void;
}

// Mirrors the server's mapping of a sign-in's client name to the assistant it is.
const signInTool = (clientName: string) => {
  if (/claude\s*code/i.test(clientName)) {
    return 'claude-code';
  }
  return /claude/i.test(clientName) ? 'claude' : 'mcp-client';
};

// A connection key lives only in this component's state and is gone once the drawer closes.
const ConnectDrawer: React.FC<ConnectDrawerProps> = ({
  projectId,
  projectName,
  connectorUrl,
  orgConnectorUrl,
  onClose,
}) => {
  const Snackbar = useSnackbarContext();
  const [selected, setSelected] = useState<Assistant | null>(null);
  const [credential, setCredential] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [showProjectUrl, setShowProjectUrl] = useState(false);
  const endpoint = guardianMcpEndpoint(window.location.origin);
  // One address for every project; the assistant chooses the project on each call.
  const signInUrl = orgConnectorUrl ?? connectorUrl;

  const { data: mine, refetch } = GQL.useMyGuardianConnectionsQuery({
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
  });
  const [connect, { loading: connecting }] = GQL.useConnectMyAssistantMutation({
    onCompleted: ({ connectMyAssistant }) => {
      setCredential(connectMyAssistant.credential);
      refetch();
    },
    onError: (err) =>
      Snackbar.open(`Could not connect: ${err.message}`, { showOk: true }),
  });
  const [revoke] = GQL.useRevokeMyGuardianConnectionMutation({
    onCompleted: () => {
      Snackbar.open('Connection revoked');
      setConfirmRevoke(null);
      refetch();
    },
    onError: (err) =>
      Snackbar.open(`Could not revoke: ${err.message}`, { showOk: true }),
  });

  const { data: signInData, refetch: refetchSignIns } =
    GQL.useMyGuardianAssistantSignInsQuery({
      fetchPolicy: 'cache-and-network',
    });
  const [revokeSignIn] = GQL.useRevokeMyGuardianAssistantSignInMutation({
    onCompleted: () => {
      Snackbar.open('Signed out');
      setConfirmRevoke(null);
      refetchSignIns();
    },
    onError: (err) =>
      Snackbar.open(`Could not sign out: ${err.message}`, { showOk: true }),
  });

  const connections = mine?.myGuardianConnections ?? [];
  const signIns = signInData?.myGuardianAssistantSignIns ?? [];
  const connectedTools = new Set([
    ...connections.map((c) => c.tool),
    ...signIns.map((s) => signInTool(s.clientName)),
  ]);

  const open = (assistant: Assistant) => {
    setCredential(null);
    setSelected(assistant);
  };

  const downloadExtension = async () => {
    setDownloading(true);
    try {
      // no-store: a cached connector would ship an outdated server inside the extension.
      const response = await fetch(WITNESS_CONNECTOR_PATH, {
        cache: 'no-store',
      });
      if (!response.ok) {
        throw new Error(`connector download failed (HTTP ${response.status})`);
      }
      const bytes = buildWitnessExtension(await response.text(), {
        mcpUrl: endpoint,
        projectId,
        projectName,
      });
      const url = URL.createObjectURL(
        new Blob([bytes], { type: 'application/zip' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = witnessExtensionFileName(projectName);
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      Snackbar.open(
        `Could not build the extension: ${
          err instanceof Error ? err.message : 'unknown error'
        }`,
        { showOk: true }
      );
    } finally {
      setDownloading(false);
    }
  };

  const renderDetail = (assistant: Assistant) => {
    const projectOnly = connectorUrl && (
      <Box sx={{ mt: 2 }}>
        <Button
          tertiary
          size="small"
          onClick={() => setShowProjectUrl((value) => !value)}
        >
          {showProjectUrl ? 'Hide' : 'Advanced: connect only this project'}
        </Button>
        {showProjectUrl && (
          <>
            <Text sz="body12" color="secondary" sx={{ mt: 1 }}>
              {`This address serves only ${projectName}, so the assistant never asks which project.`}
            </Text>
            <Snippet text={connectorUrl} label="Copy project URL" />
          </>
        )}
      </Box>
    );
    if (assistant.key === 'claude' && signInUrl) {
      return (
        <>
          <Text sz="body14" color="secondary">
            No key to copy: Claude signs in with your Fairly account, and one
            connector covers every project you can access.
          </Text>
          <Snippet text={signInUrl} label="Copy URL" />
          <Steps
            steps={[
              'In Claude, open Settings, then Connectors.',
              'Choose Add custom connector and paste the URL above.',
              'Click Connect, sign in to Fairly if asked, and choose Allow.',
              'Ask Claude which projects you can use, then ask it to check policy.',
            ]}
          />
          <Text sz="body12" color="secondary">
            On a Claude Team or Enterprise plan, an Owner adds the connector
            once under Organization settings, Connectors, Add, Custom, Web.
            Everyone then clicks Connect in their own Connectors list.
          </Text>
          {projectOnly}
          {isLocalAddress(signInUrl) && (
            <Text sz="body12" color="secondary">
              This is a local address. Claude reaches connectors from the
              internet, so this works once Fairly runs on a public https
              address. Claude Code works locally today.
            </Text>
          )}
        </>
      );
    }
    if (assistant.key === 'claude-code' && signInUrl) {
      return (
        <>
          <Text sz="body14">Run this in your terminal:</Text>
          <Snippet
            text={`claude mcp add --transport http asenion-witness ${signInUrl}`}
            label="Copy command"
          />
          <Steps
            steps={[
              'Start Claude Code and type /mcp.',
              'Choose asenion-witness, then Authenticate.',
              'Sign in to Fairly in the browser that opens and choose Allow.',
            ]}
          />
          {projectOnly}
        </>
      );
    }
    if (!assistant.keyClient) {
      return (
        <Text sz="body14" color="secondary">
          Signing in with Fairly needs this server to know its public address
          (BASE_URL). Ask an administrator to set it.
        </Text>
      );
    }
    if (!credential) {
      return (
        <>
          <Text sz="body14" color="secondary">
            {assistant.key === 'claude-desktop'
              ? 'Installs as a Claude Desktop extension that reaches Fairly from your computer, including a local Fairly.'
              : 'For MCP clients that cannot sign in with Fairly. You get a connection key to paste into their configuration.'}
          </Text>
          <Button
            sx={{ mt: 2 }}
            loading={connecting}
            onClick={() => {
              if (!connecting && assistant.keyClient) {
                connect({
                  variables: {
                    input: { projectId, client: assistant.keyClient },
                  },
                });
              }
            }}
          >
            Create connection key
          </Button>
        </>
      );
    }
    return (
      <>
        <Text sz="bold-body14">Your connection key</Text>
        <Text sz="body12" color="secondary">
          Shown only once. Keep it private, as anyone with it can act as you on
          this project.
        </Text>
        <Snippet text={credential} label="Copy key" />
        {assistant.key === 'claude-desktop' ? (
          <>
            <Steps
              steps={[
                'Download the extension.',
                'Double-click it and choose Install in Claude Desktop.',
                'Paste the connection key when Claude asks for it.',
              ]}
            />
            <Button loading={downloading} onClick={downloadExtension}>
              Download extension
            </Button>
          </>
        ) : (
          <>
            <Text sz="body14">
              Add this server to your MCP client configuration:
            </Text>
            <Snippet
              text={mcpClientConfig(endpoint, credential)}
              label="Copy configuration"
            />
          </>
        )}
      </>
    );
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Connect your AI assistant"
      width={640}
    >
      <Box sx={{ p: 2 }}>
        {selected ? (
          <>
            <Button
              tertiary
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setSelected(null)}
            >
              All assistants
            </Button>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}
            >
              <IconTile icon={selected.icon} />
              <Text sz="bold-body16">{selected.name}</Text>
            </Box>
            {renderDetail(selected)}
          </>
        ) : (
          <>
            <Text sz="body14" color="secondary" sx={{ mb: 2 }}>
              {`Your assistant receives ${projectName}'s published policies, and every consult is recorded under your name.`}
            </Text>
            <Box
              role="table"
              aria-label="AI assistants"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                columnGap: 2,
                alignItems: 'center',
              }}
            >
              {['Assistant', 'Type', 'Status'].map((heading) => (
                <Text
                  key={heading}
                  sz="body12"
                  color="secondary"
                  sx={{
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {heading}
                </Text>
              ))}
              {ASSISTANTS.map((assistant) => {
                const unavailable = assistant.signsIn && !signInUrl;
                const connected = connectedTools.has(assistant.key);
                const cell = {
                  py: 1.25,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  minHeight: 56,
                  display: 'flex',
                  alignItems: 'center',
                };
                return (
                  <React.Fragment key={assistant.key}>
                    <Box sx={{ ...cell, gap: 1.5, minWidth: 0 }}>
                      <IconTile icon={assistant.icon} />
                      <Text sz="body14" noWrap>
                        {assistant.name}
                      </Text>
                      {assistant.badge && (
                        <Chip
                          label={assistant.badge}
                          size="small"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                    </Box>
                    <Box sx={cell}>
                      <Text sz="body14" color="secondary" noWrap>
                        {assistant.type}
                      </Text>
                    </Box>
                    <Box sx={{ ...cell, justifyContent: 'flex-end', gap: 1 }}>
                      {connected && (
                        <Tooltip title="Connected">
                          <CheckIcon fontSize="small" aria-label="Connected" />
                        </Tooltip>
                      )}
                      <Tooltip
                        title={
                          unavailable
                            ? 'Needs this server’s public address (BASE_URL)'
                            : ''
                        }
                      >
                        <span>
                          <Button
                            outlined
                            size="small"
                            disabled={unavailable}
                            onClick={() => open(assistant)}
                          >
                            {connected ? 'Add' : 'Connect'}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />
        <Text sz="bold-body14">Your connections</Text>
        {!connections.length && !signIns.length && (
          <Text sz="body14" color="secondary">
            None yet.
          </Text>
        )}
        {signIns.map((signIn) => (
          <Box
            key={signIn._id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Text sz="body14">{`${signIn.clientName} · all your projects`}</Text>
              <Text sz="body12" color="secondary">
                {signIn.lastUsedAt
                  ? `Signed in with Fairly · last used ${new Date(signIn.lastUsedAt).toLocaleString()}`
                  : 'Signed in with Fairly · not used yet'}
              </Text>
            </Box>
            <Button
              outlined
              size="small"
              onClick={() => {
                if (confirmRevoke === signIn._id) {
                  revokeSignIn({ variables: { _id: signIn._id } });
                } else {
                  setConfirmRevoke(signIn._id);
                }
              }}
            >
              {confirmRevoke === signIn._id ? 'Confirm sign out' : 'Sign out'}
            </Button>
          </Box>
        ))}
        {connections.map((connection) => (
          <Box
            key={connection._id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Text sz="body14">{connection.name}</Text>
              <Text sz="body12" color="secondary">
                {connection.lastUsedAt
                  ? `Last used ${new Date(connection.lastUsedAt).toLocaleString()}`
                  : 'Not used yet'}
              </Text>
            </Box>
            <Button
              outlined
              size="small"
              onClick={() => {
                if (confirmRevoke === connection._id) {
                  revoke({ variables: { _id: connection._id } });
                } else {
                  setConfirmRevoke(connection._id);
                }
              }}
            >
              {confirmRevoke === connection._id ? 'Confirm revoke' : 'Revoke'}
            </Button>
          </Box>
        ))}
      </Box>
    </Drawer>
  );
};

export interface ConnectAssistantProps {
  projectId: string;
  projectName: string;
}

const ConnectAssistant: React.FC<ConnectAssistantProps> = ({
  projectId,
  projectName,
}) => {
  const [open, setOpen] = useState(false);
  const { data, error } = GQL.useGuardianAssistantAvailabilityQuery({
    variables: { projectId },
    skip: !projectId,
    fetchPolicy: 'cache-and-network',
  });

  // Without project access the query fails, and there is nothing to offer.
  if (error || !projectId || !data) {
    return null;
  }
  const { available, reason, connectorUrl, orgConnectorUrl } =
    data.guardianAssistantAvailability;

  return (
    <>
      <Tooltip title={available ? '' : (reason ?? '')}>
        {/* A disabled button fires no hover events, so the tooltip needs a wrapper. */}
        <span>
          <Button
            outlined
            size="small"
            disabled={!available}
            onClick={() => setOpen(true)}
          >
            Connect your AI assistant
          </Button>
        </span>
      </Tooltip>
      {open && (
        <ConnectDrawer
          projectId={projectId}
          projectName={projectName}
          connectorUrl={connectorUrl ?? null}
          orgConnectorUrl={orgConnectorUrl ?? null}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default ConnectAssistant;
